// Static blog posts for the public site.
// Posts may use either the legacy `sections[]` format (paragraph arrays) or
// the newer `body` field — a Markdown-flavoured long-form string parsed by
// `<RichContent />`. Prefer `body` for any new post.

import type { AuthoritySource } from "@/components/site/AuthoritySources";

export interface BlogPostSection {
  heading?: string;
  paragraphs: string[];
}

export interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  date: string;
  /** Optional ISO date of last substantive revision. */
  dateModified?: string;
  author: string;
  category: string;
  readTime: string;
  /** Legacy structured sections. Falls back when `body` is absent. */
  sections?: BlogPostSection[];
  /** Long-form Markdown-ish body (preferred for new posts). */
  body?: string;
  /** Optional AEO Quick Answer block rendered at the top of the article. */
  quickAnswer?: {
    answer: string;
    bullets?: string[];
  };
  /** Optional outbound primary sources for E-E-A-T. */
  sources?: AuthoritySource[];
  /** Slugs of related posts to surface at the bottom. */
  relatedSlugs?: string[];
  meta: { title: string; description: string };
  /** Optional featured image (public path or absolute URL). */
  image?: string;
  /** Raw HTML body — used for posts imported from WordPress. When present,
   *  BlogPostPage renders this via a sanitized prose container instead of Markdown. */
  bodyHtml?: string;
  /** Marks posts sourced from the WordPress sync. */
  source?: "static" | "wordpress";
}

import { importedBlogPosts } from "./blogImported";

const authoredPosts: BlogPost[] = [
  {
    slug: "how-an-offer-in-compromise-actually-works",
    title: "How an Offer in Compromise Actually Works (and Who Qualifies)",
    excerpt:
      "The IRS settles tax debts for less than what's owed every day — but only when the offer is built correctly. Here's how the OIC program actually works.",
    date: "2025-05-20",
    dateModified: "2026-04-10",
    author: "Franklin Sofi, CPA, CTRS",
    category: "Tax Debt Relief",
    readTime: "8 min read",
    quickAnswer: {
      answer:
        "An IRS Offer in Compromise (OIC) lets you settle your tax debt for less than the full balance when your Reasonable Collection Potential — net equity in assets plus a multiple of monthly disposable income — is less than what you owe. About one-third of submitted offers are accepted; professionally built offers perform meaningfully better.",
      bullets: [
        "Three grounds: doubt as to collectibility, doubt as to liability, effective tax administration.",
        "Calculation uses IRS Allowable Living Expense standards — not what you actually spend.",
        "Submission filing fee: $205 plus a 20% down payment for lump-sum offers (or first installment for periodic-payment offers).",
        "Average decision time: 6–12 months. Collections are generally paused while the offer is pending.",
      ],
    },
    body: `If you owe the IRS more than you can realistically pay, you've probably heard about the **Offer in Compromise** — the program that lets qualifying taxpayers settle their debt for less than the full amount. What you may not know is how strict the program is, and why a meaningful share of self-submitted offers are rejected.

## What an Offer in Compromise really is

An OIC is a contract with the IRS. You agree to pay a specific amount — calculated from your future income and net equity in assets — and the IRS agrees to wipe out the rest. The legal basis is [IRC § 7122](https://www.law.cornell.edu/uscode/text/26/7122) and the program is implemented in [IRS Form 656 and Form 433-A(OIC)](https://www.irs.gov/forms-pubs/about-form-656).

There are three grounds for an OIC:

1. **Doubt as to collectibility** — you can't pay the full debt before the 10-year collection statute expires. This is by far the most common ground.
2. **Doubt as to liability** — you don't actually owe what the IRS says you owe.
3. **Effective tax administration** — paying would create economic hardship or be unfair given exceptional facts.

## How the IRS calculates your offer amount

The formula is called **Reasonable Collection Potential (RCP)**:

> RCP = Net realizable equity in assets + (Monthly disposable income × 12 or 24)

Disposable income is your gross monthly income **minus** [IRS Allowable Living Expense standards](https://www.irs.gov/businesses/small-businesses-self-employed/collection-financial-standards) — national and local, not what you actually spend. Net realizable equity is roughly fair-market value × 80% minus secured debt.

| Payment option | Multiplier | Submission requirement |
| --- | --- | --- |
| Lump-sum cash (5 months) | 12× monthly disposable income | 20% down + $205 application fee |
| Periodic payment (24 months) | 24× monthly disposable income | First payment + $205 application fee |

This is where most self-prepared offers go wrong — overstating income, understating allowable expenses, or missing asset exclusions can move your offer by tens of thousands of dollars.

## Why representation matters

We routinely see offers fail because the taxpayer:

- Overstated income by including non-recurring items.
- Understated allowable expenses (medical, childcare, secured debt payments).
- Missed exclusions for retirement accounts in active distribution status.
- Filed an offer for a debt amount they didn't actually owe.

A correctly built OIC presents the strongest defensible numbers — and the difference is often the difference between an acceptance at $4,000 and a rejection on a $90,000 balance.

## What happens after you submit

The IRS has 24 months to accept, reject, or return your offer. During that window:

- Collection activity (levies, garnishments) is generally paused.
- The 10-year collection statute is suspended for the duration of consideration plus 30 days.
- You must stay compliant — file every return and make every estimated payment on time. Missing one ends the offer immediately.

If accepted, you have five years of strict compliance ahead. Miss a filing or fall behind on a payment plan, and the original debt is reinstated.

## Bottom line

The Offer in Compromise is real, it works, and it's one of the most powerful tools in tax resolution. But it's not for everyone, and the calculation is unforgiving. Before you submit one, get it pre-qualified by a credentialed professional. If you'd like a free pre-qualification, call us at (214) 646-3802.`,
    sources: [
      {
        label: "Form 656 Booklet — Offer in Compromise",
        url: "https://www.irs.gov/pub/irs-pdf/f656b.pdf",
        publisher: "IRS",
      },
      {
        label: "Collection Financial Standards",
        url: "https://www.irs.gov/businesses/small-businesses-self-employed/collection-financial-standards",
        publisher: "IRS",
      },
      {
        label: "26 U.S. Code § 7122 — Compromises",
        url: "https://www.law.cornell.edu/uscode/text/26/7122",
        publisher: "Cornell Legal Information Institute",
      },
    ],
    relatedSlugs: [
      "what-to-do-when-you-get-a-cp2000-notice",
      "wage-garnishment-how-to-stop-it-fast",
      "irs-statute-of-limitations-csed-explained",
    ],
    meta: {
      title: "How an Offer in Compromise Actually Works",
      description:
        "Inside the IRS Offer in Compromise program — how qualification works, how the offer amount is calculated, and why most self-submitted offers fail.",
    },
  },

  {
    slug: "what-to-do-when-you-get-a-cp2000-notice",
    title: "What To Do When You Get a CP2000 Notice",
    excerpt:
      "A CP2000 looks like a bill, but it's actually a proposal. Respond correctly within 30 days and you can often eliminate the proposed tax entirely.",
    date: "2025-03-14",
    dateModified: "2026-02-10",
    author: "Franklin Sofi, CPA, CTRS",
    category: "IRS Notices",
    readTime: "6 min read",
    quickAnswer: {
      answer:
        "A CP2000 is not a bill and not an audit — it's an automated proposed adjustment when third-party income reports (1099s, W-2s) don't match what you filed. You have 30 days to agree, partially agree, or dispute. A documented response often eliminates the proposed tax entirely.",
      bullets: [
        "Reply within 30 days using the Response form included with the notice.",
        "Don't sign 'I agree' if the IRS-proposed numbers ignore your cost basis or deductions.",
        "Ignoring it triggers a Statutory Notice of Deficiency (90-day Tax Court window).",
      ],
    },
    body: `Few IRS notices cause more panic than a **CP2000**. The headline number can be tens of thousands of dollars, the response window is short, and the language is intimidating. But here's the truth: a CP2000 is **not a bill** and **not an audit**. It's a proposal.

## What the CP2000 actually means

The IRS receives 1099s, W-2s, K-1s, and other information returns from third parties. When something doesn't match what you reported, the [Automated Underreporter (AUR) system](https://www.irs.gov/individuals/understanding-your-cp2000-notice) generates a CP2000 with proposed adjustments. The IRS hasn't audited you. They haven't verified that the numbers in their system are right. They're showing you what their data says and asking you to confirm, dispute, or partially agree.

## Your three response options

1. **Agree.** Sign and return the Response form. Pay the balance or set up an installment plan.
2. **Partially agree.** Agree to some adjustments, dispute others. Include a written explanation and supporting documentation.
3. **Disagree entirely.** Provide documentation showing the IRS's proposed adjustments are wrong.

## The single biggest mistake

Ignoring the notice. If you don't respond within 30 days, the IRS issues a **Statutory Notice of Deficiency** (also called a "90-day letter"). At that point you have 90 days to petition the U.S. Tax Court — or the proposed tax becomes a real assessment. From there, you're either paying the bill or fighting it in Tax Court.

| Stage | Document | Deadline |
| --- | --- | --- |
| Proposed adjustment | CP2000 | 30 days to respond |
| Final notice before assessment | Statutory Notice of Deficiency (CP3219A / Letter 531) | 90 days to petition Tax Court |
| Assessed liability | CP14, then collection notices | Collection begins |

## How a proper response works

Most CP2000s involve **missing cost basis** on investment sales or **1099-K** activity from payment processors. We pull your records, calculate the actual taxable amount, and submit a written response with supporting documentation. In many cases, the resulting tax is dramatically lower than the original proposal — sometimes zero.

A clean response includes:

- A signed CP2000 Response form indicating partial or total disagreement.
- A short cover letter explaining the position.
- Schedule-by-schedule recalculation showing the corrected numbers.
- Source documents — brokerage 1099-B with cost basis, corrected 1099, expense substantiation.

## Don't agree by default

If your CP2000 is wrong, you have the right to dispute it. If you'd like help, call **(214) 646-3802** — we respond to CP2000s every week.`,
    sources: [
      {
        label: "Understanding Your CP2000 Notice",
        url: "https://www.irs.gov/individuals/understanding-your-cp2000-notice",
        publisher: "IRS",
      },
      {
        label: "Form 12203 — Request for Appeals Review",
        url: "https://www.irs.gov/forms-pubs/about-form-12203",
        publisher: "IRS",
      },
    ],
    relatedSlugs: [
      "how-an-offer-in-compromise-actually-works",
      "irs-statute-of-limitations-csed-explained",
      "owe-the-irs-more-than-10000",
    ],
    meta: {
      title: "What To Do When You Get a CP2000 Notice",
      description:
        "How to respond to an IRS CP2000 underreporter notice and avoid a wrongful tax assessment. Step-by-step from a CPA.",
    },
  },

  {
    slug: "wage-garnishment-how-to-stop-it-fast",
    title: "IRS Wage Garnishment: How To Stop It Fast",
    excerpt:
      "An IRS wage garnishment can take a substantial portion of your paycheck. Here's how it gets released — usually within 24 to 72 hours of the right action.",
    date: "2025-01-22",
    dateModified: "2026-01-30",
    author: "Franklin Sofi, CPA, CTRS",
    category: "Collections Defense",
    readTime: "5 min read",
    quickAnswer: {
      answer:
        "Filing Form 2848 (Power of Attorney) and putting an acceptable resolution in place — installment agreement, Currently Not Collectible status, or a pending Offer in Compromise — typically releases an IRS wage levy within 24 to 72 hours.",
      bullets: [
        "Authority: IRC § 6343(a) requires release when the levy creates economic hardship.",
        "Your employer cannot stop the levy on their own — it must be released by the IRS in writing.",
        "Time matters: each pay cycle without action is money you don't get back without an OIC or refund.",
      ],
    },
    body: `An IRS wage garnishment is one of the most aggressive collection tools the government has. It's continuous, it's automatic, and it can leave you with almost nothing to live on. The good news: it can almost always be released — and faster than you think.

## How garnishment happens

Before the IRS levies wages, they send a series of notices culminating in a **Final Notice of Intent to Levy** (LT11 or Letter 1058). You have 30 days to request a **Collection Due Process (CDP) hearing** under [IRC § 6330](https://www.law.cornell.edu/uscode/text/26/6330). If you miss it, the IRS contacts your employer with Form 668-W and your next paycheck shrinks.

The amount exempt from the levy is set by [Publication 1494](https://www.irs.gov/pub/irs-pdf/p1494.pdf) and depends on your filing status and dependents — often only a few hundred dollars per pay period.

## The fastest path to release

The IRS will release a wage levy when an acceptable resolution is in place. Common options:

| Resolution | Typical release time | Best for |
| --- | --- | --- |
| Installment Agreement | 24–48 hours | Stable income, balance under $50K |
| Currently Not Collectible | 48–72 hours | Income at or below allowable expenses |
| Accepted or pending OIC | 24–72 hours | RCP below total liability |
| Economic hardship release (IRC § 6343(a)) | Often same-day with documentation | Levy itself causing inability to meet basic living expenses |

Once Power of Attorney (Form 2848) is filed and the right paperwork hits the right desk, releases commonly happen within **24 to 72 hours**.

## What not to do

- **Don't call your employer** asking them to stop sending the money — they're legally required to comply with the levy under IRC § 6332.
- **Don't ignore the IRS phone calls** thinking it'll go away.
- **Don't try to negotiate a settlement first** — the levy needs to be released before any productive resolution conversation makes sense.

## Get help today

If your wages are being garnished right now, time matters. Call **(214) 646-3802** and we'll get a Power of Attorney filed today.`,
    sources: [
      {
        label: "Information About Wage Levies",
        url: "https://www.irs.gov/businesses/small-businesses-self-employed/information-about-wage-levies",
        publisher: "IRS",
      },
      {
        label: "Publication 1494 — Tables for Figuring Amount Exempt from Levy",
        url: "https://www.irs.gov/pub/irs-pdf/p1494.pdf",
        publisher: "IRS",
      },
      {
        label: "IRC § 6343 — Authority to release levy",
        url: "https://www.law.cornell.edu/uscode/text/26/6343",
        publisher: "Cornell Legal Information Institute",
      },
    ],
    relatedSlugs: [
      "irs-cp504-notice-what-it-means",
      "owe-the-irs-more-than-10000",
      "how-an-offer-in-compromise-actually-works",
    ],
    meta: {
      title: "How To Stop an IRS Wage Garnishment Fast",
      description:
        "Step-by-step on how to release an IRS wage garnishment within days — from a CPA tax resolution specialist.",
    },
  },

  // ---------- New rich posts ----------

  {
    slug: "owe-the-irs-more-than-10000",
    title: "Owe the IRS More Than $10,000? Here's What Actually Happens",
    excerpt:
      "Crossing $10,000 in IRS debt triggers automatic enforcement: federal tax liens, Revenue Officer assignment, and at higher balances, passport revocation. Here's how to get ahead of it.",
    date: "2026-03-25",
    dateModified: "2026-03-25",
    author: "Franklin Sofi, CPA, CTRS",
    category: "Tax Debt Relief",
    readTime: "9 min read",
    quickAnswer: {
      answer:
        "Once your IRS balance crosses $10,000, the agency can file a Notice of Federal Tax Lien automatically. Above approximately $62,000 (2026 threshold, adjusted for inflation), your debt is certified as 'seriously delinquent' under IRC § 7345 and your passport can be denied or revoked. Resolution options expand at this level — installment agreements, Offers in Compromise, and Currently Not Collectible status are all on the table.",
      bullets: [
        "Pull your IRS account transcript before responding to anything.",
        "Don't fill out Form 433-A without help — it determines every settlement you're offered.",
        "Streamlined Installment Agreement: balances under $50K can pay over 72 months with minimal disclosure.",
      ],
    },
    body: `Crossing the **$10,000 threshold** with the IRS is a real turning point. Below that number, the IRS mostly sends letters. Above it, things change: a federal tax lien can be filed automatically, your case can be assigned to a Revenue Officer, and at higher balances your passport can be flagged for revocation under [IRC § 7345](https://www.law.cornell.edu/uscode/text/26/7345).

If you've just learned you owe five figures or more, here's what experienced resolution professionals actually do, step by step.

## Pull your IRS account transcript first

Before you respond to anything, you need to know **what the IRS knows**. Order three transcripts for every year in question:

- **Account transcript** — assessments, payments, penalties, lien filings, levy actions.
- **Wage & income transcript** — every 1099, W-2, and K-1 the IRS has on file.
- **Record of account** — combined view used to verify CSED dates.

We see clients all the time whose actual balance is wildly different from the notice — often because of unfiled returns being filed as **Substitute for Return (SFR)** without any of their deductions or credits.

## Beware the $10,000 federal tax lien trigger

Once an assessed balance crosses $10,000 (and the case meets internal IRS criteria), the agency files a [Notice of Federal Tax Lien](https://www.irs.gov/businesses/small-businesses-self-employed/understanding-a-federal-tax-lien) in the county where you live. That single document:

- Destroys your credit score, often by 100+ points.
- Attaches to every piece of property you own — house, car, even after-acquired property.
- Prevents you from refinancing, selling, or getting a HELOC.
- Can be discovered by future employers, landlords, and licensing boards.

A credentialed representative can sometimes prevent the lien from being filed in the first place, or get it withdrawn under the **Fresh Start Initiative** once you're in an Installment Agreement.

## Resolution options at this debt level

| Option | Best for | Typical timeline |
| --- | --- | --- |
| Streamlined IA | Balance under $50K, stable income | 30–60 days |
| Non-Streamlined IA | Balance $50K–$250K | 60–120 days (financial disclosure required) |
| Partial Pay IA | Income below allowable + 10 years until CSED | 90–180 days |
| Currently Not Collectible | Forced payment would prevent basic expenses | 30–90 days |
| Offer in Compromise | RCP below total liability | 6–12 months |

## The passport revocation risk

Once your balance crosses the inflation-adjusted threshold (about **$62,000 in 2026**), the IRS certifies your debt as "seriously delinquent" to the State Department. Your passport application can be denied. Your existing passport can be revoked. The only way to clear it is to get into a formal resolution agreement and have the IRS reverse the certification.

## Don't file a 433-A without help

[Form 433-A](https://www.irs.gov/pub/irs-pdf/f433a.pdf) (or 433-F for streamlined cases, 433-B for businesses) is the financial disclosure that determines every settlement you'll be offered. Common mistakes that cost taxpayers tens of thousands:

- Overstating equity in a vehicle that has secured debt.
- Missing allowable medical, childcare, or court-ordered payments.
- Misclassifying retirement accounts in active distribution status.
- Reporting gross income instead of net self-employment income.

This is the single most important document in your case. Don't fill it out alone.

## What to do this week

1. **Pull your transcripts** — IRS [Get Transcript](https://www.irs.gov/individuals/get-transcript) or via Form 4506-T.
2. **Don't ignore the notices** — every notice starts a different clock.
3. **Don't make a payment plan over the phone** with the first IRS agent who calls — it locks you into terms that may not be the best available.
4. **Call a credentialed CPA** for a free consultation, get a real plan, and start fighting back.`,
    sources: [
      {
        label: "Understanding a Federal Tax Lien",
        url: "https://www.irs.gov/businesses/small-businesses-self-employed/understanding-a-federal-tax-lien",
        publisher: "IRS",
      },
      {
        label: "Revocation or Denial of Passport in Case of Certain Tax Delinquencies",
        url: "https://www.irs.gov/businesses/small-businesses-self-employed/revocation-or-denial-of-passport-in-case-of-certain-unpaid-taxes",
        publisher: "IRS",
      },
      {
        label: "26 U.S. Code § 7345",
        url: "https://www.law.cornell.edu/uscode/text/26/7345",
        publisher: "Cornell Legal Information Institute",
      },
    ],
    relatedSlugs: [
      "how-an-offer-in-compromise-actually-works",
      "wage-garnishment-how-to-stop-it-fast",
      "irs-statute-of-limitations-csed-explained",
    ],
    meta: {
      title: "Owe the IRS More Than $10,000? Here's What Actually Happens",
      description:
        "What happens when your IRS balance crosses $10,000: liens, Revenue Officers, passport revocation — plus every resolution option a CPA actually uses.",
    },
  },

  {
    slug: "irs-statute-of-limitations-csed-explained",
    title: "The IRS 10-Year Collection Statute (CSED): Why It's the Most Underused Tool in Tax Resolution",
    excerpt:
      "The IRS has 10 years to collect — and partial-pay installment agreements that cross the CSED can erase huge balances for pennies on the dollar.",
    date: "2026-02-12",
    dateModified: "2026-02-12",
    author: "Franklin Sofi, CPA, CTRS",
    category: "Tax Debt Relief",
    readTime: "7 min read",
    quickAnswer: {
      answer:
        "The IRS has 10 years from the date of assessment to collect a tax debt. After the Collection Statute Expiration Date (CSED), the debt is legally extinguished. Events like an Offer in Compromise, bankruptcy, or a CDP appeal toll (pause) the clock. A correctly built Partial Pay Installment Agreement that runs out the remaining CSED is one of the most powerful tools in resolution.",
      bullets: [
        "Authority: IRC § 6502(a)(1) — 10-year limitations period.",
        "Each assessment year has its own CSED — different tax years expire at different times.",
        "Tolling events include pending OICs, bankruptcy stays, CDP requests, and time abroad.",
      ],
    },
    body: `Most taxpayers who owe the IRS have no idea that the agency's collection power has an **expiration date**. The Collection Statute Expiration Date — the **CSED** — is the IRS's most underused weakness, and a properly built strategy around it can erase six-figure balances for cents on the dollar.

## What the CSED is

[IRC § 6502(a)(1)](https://www.law.cornell.edu/uscode/text/26/6502) gives the IRS **10 years from the date of assessment** to collect a tax debt. After that date passes, the debt is legally extinguished. The IRS must remove it from your account.

A few critical points most people miss:

- The clock starts at **assessment**, not when the return was filed and not when the tax year ended.
- Each assessment has its **own CSED** — a 2014 balance and a 2017 balance expire on different days.
- The IRS calculates and tracks CSEDs internally; you can verify them on your **account transcript**.

## What pauses the clock (tolling events)

The CSED is **suspended** during certain events. The clock resumes after they end, often plus a defined extension period:

| Event | Suspends CSED? | Notes |
| --- | --- | --- |
| Pending Offer in Compromise | Yes | Duration of consideration plus 30 days |
| Pending CDP appeal | Yes | While appeal is open plus 90 days |
| Bankruptcy automatic stay | Yes | Duration of stay plus 6 months |
| Pending Installment Agreement request | Yes | While under consideration |
| Living outside the U.S. continuously > 6 months | Yes | IRC § 6503(c) |
| Innocent Spouse Relief request | Yes | While pending |

A request that is **withdrawn, returned, or rejected** still tolls the clock for the time it was pending. This is why an OIC strategy needs to weigh the toll against the benefit.

## The Partial Pay Installment Agreement strategy

A **Partial Pay Installment Agreement (PPIA)** is a monthly payment plan calibrated to what you can actually afford — **not** what's required to fully pay the debt before the CSED. As the CSED approaches, the unpaid balance is forgiven by operation of law.

> Example: A $180,000 balance with eight years left on the CSED. After an IRS financial review, the monthly disposable income is $400. A PPIA at $400/month pays roughly $38,400 over the remaining CSED. The remaining ~$141,600 expires.

The math beats most Offers in Compromise — without the application fee, without the down payment, without the strict five-year compliance window. The trade-off: the IRS reviews PPIAs every two years and can increase payments if your income rises.

## When CSED strategy beats an OIC

| Situation | Better tool |
| --- | --- |
| Income low, no significant assets, > 5 years to CSED | **PPIA** |
| Lump sum or future asset coming, < 5 years to CSED | **OIC** |
| Income volatile, business owner | **PPIA** (lower compliance risk) |
| Want to clear the debt off the books quickly | **OIC** |

## Don't restart the clock by accident

The single worst thing you can do is sign a waiver extending the CSED. The IRS sometimes requests this in installment agreement negotiations. **Never sign Form 900** (Tax Collection Waiver) without understanding exactly how many days you're giving up.

## How to find your CSED

The CSED for each assessment is listed on your IRS **account transcript** under transaction code 150 (assessment). Use the assessment date — not the return due date — and add 10 years, then adjust for any tolling events shown on the transcript (codes 480, 520, 971, etc.).

If you've been carrying IRS debt for years and never had someone calculate your CSEDs, that's the first call we'd make on your case. Reach us at **(214) 646-3802**.`,
    sources: [
      {
        label: "IRC § 6502 — Collection after assessment",
        url: "https://www.law.cornell.edu/uscode/text/26/6502",
        publisher: "Cornell Legal Information Institute",
      },
      {
        label: "IRM 5.14.2 — Partial Payment Installment Agreements",
        url: "https://www.irs.gov/irm/part5/irm_05-014-002",
        publisher: "Internal Revenue Manual",
      },
      {
        label: "Get an IRS Account Transcript",
        url: "https://www.irs.gov/individuals/get-transcript",
        publisher: "IRS",
      },
    ],
    relatedSlugs: [
      "how-an-offer-in-compromise-actually-works",
      "owe-the-irs-more-than-10000",
      "irs-cp504-notice-what-it-means",
    ],
    meta: {
      title: "The IRS 10-Year Collection Statute (CSED) Explained",
      description:
        "How the IRS Collection Statute Expiration Date works, what pauses the clock, and why a Partial Pay Installment Agreement often beats an Offer in Compromise.",
    },
  },

  {
    slug: "irs-cp504-notice-what-it-means",
    title: "Got a CP504 Notice? What It Means and What To Do in the Next 30 Days",
    excerpt:
      "A CP504 is the IRS's final warning before levy. The exact next step depends on whether the notice is for a state refund offset or a true Final Notice of Intent to Levy.",
    date: "2026-01-08",
    dateModified: "2026-01-08",
    author: "Franklin Sofi, CPA, CTRS",
    category: "IRS Notices",
    readTime: "6 min read",
    quickAnswer: {
      answer:
        "A CP504 is the IRS's Notice of Intent to Levy state tax refunds and search for other assets. It is NOT the Final Notice of Intent to Levy under IRC § 6330 — that's a separate LT11 / Letter 1058 that triggers Collection Due Process rights. You typically have 30 days to act before enforcement escalates.",
      bullets: [
        "CP504 = state-refund seizure authority; LT11/Letter 1058 = full levy authority with CDP rights.",
        "Filing Form 2848 and putting a resolution in place stops further escalation.",
        "Ignoring CP504 is the #1 path to wage garnishment and bank levy within 60–90 days.",
      ],
    },
    body: `A **CP504** in your mailbox is a serious warning, but it's widely misunderstood — and the right response depends on what type of notice it actually is.

## What the CP504 actually does

A CP504 (sometimes labeled CP504B for businesses) is the IRS's **Notice of Intent to Seize (Levy) Your State Tax Refund**. Under [IRC § 6331(d)](https://www.law.cornell.edu/uscode/text/26/6331), this gives the IRS authority to:

- Seize any state income tax refund you're entitled to.
- Search for and levy other assets (bank accounts, accounts receivable).
- File a Notice of Federal Tax Lien if not already filed.

What it does **not** do — yet — is trigger the **Collection Due Process (CDP)** rights that come with a [Final Notice of Intent to Levy (LT11 or Letter 1058)](https://www.irs.gov/individuals/understanding-your-letter-1058-or-lt11). CDP gives you the right to appeal to the Office of Appeals and request alternative collection measures.

## The notice sequence

| Notice | What it means | Time you have |
| --- | --- | --- |
| CP14 | Initial balance due | Pay or set up plan |
| CP501 / CP503 | Reminder notices | Increasing urgency |
| **CP504** | Intent to levy state refund + asset search | ~30 days before escalation |
| LT11 / Letter 1058 | **Final** Notice of Intent to Levy + CDP rights | 30 days to file Form 12153 |
| Form 668-A / 668-W | Actual levy on bank / wages | Levy in effect |

## What to do in the next 30 days

1. **Pull your account transcript.** Verify the balance and confirm whether an LT11 has also been issued.
2. **Don't pay blindly.** If the balance is wrong (common with SFR filings or missed amendments), paying locks it in.
3. **Don't sign Form 433 over the phone.** A Revenue Officer will use it to set up the **wrong** resolution if you're not represented.
4. **File Form 2848 (Power of Attorney)** so a CPA can handle every contact going forward.
5. **Choose a resolution** — installment agreement, CNC, OIC, or appeals — based on the facts.

## How to stop escalation

The IRS will stop escalating collection actions once an **acceptable resolution** is in place. The fastest paths:

- **Streamlined Installment Agreement** if your balance is under $50,000.
- **Currently Not Collectible (CNC)** if a payment would prevent you from meeting basic living expenses under [Collection Financial Standards](https://www.irs.gov/businesses/small-businesses-self-employed/collection-financial-standards).
- **Offer in Compromise** if your Reasonable Collection Potential is below your balance.

## What ignoring a CP504 actually triggers

Most CP504 recipients who do nothing see one of three things within 60 to 90 days:

- A **state refund offset** that's small but immediate.
- An **LT11 / Letter 1058** giving 30 days to file a CDP appeal.
- A **bank levy or wage garnishment** if the LT11's 30-day window also lapses.

If your CP504 just arrived, call **(214) 646-3802**. We can have a Power of Attorney filed today.`,
    sources: [
      {
        label: "Understanding Your CP504 Notice",
        url: "https://www.irs.gov/individuals/understanding-your-cp504-notice",
        publisher: "IRS",
      },
      {
        label: "Letter 1058 / LT11 — Final Notice of Intent to Levy",
        url: "https://www.irs.gov/individuals/understanding-your-letter-1058-or-lt11",
        publisher: "IRS",
      },
      {
        label: "Form 12153 — Request for Collection Due Process Hearing",
        url: "https://www.irs.gov/forms-pubs/about-form-12153",
        publisher: "IRS",
      },
    ],
    relatedSlugs: [
      "wage-garnishment-how-to-stop-it-fast",
      "owe-the-irs-more-than-10000",
      "irs-statute-of-limitations-csed-explained",
    ],
    meta: {
      title: "What an IRS CP504 Notice Means and How To Respond",
      description:
        "The CP504 is the IRS's final warning before levy — but it's not the LT11. Here's what each notice means and how to stop enforcement within 30 days.",
    },
  },

  {
    slug: "irs-back-taxes-unfiled-returns-survival-guide",
    title: "Unfiled Tax Returns: The Survival Guide for Catching Up Without Getting Crushed",
    excerpt:
      "The IRS only requires six years of back returns to be considered compliant. File them in the right order, with the right documentation, and most cases resolve cleanly.",
    date: "2025-11-04",
    dateModified: "2025-11-04",
    author: "Franklin Sofi, CPA, CTRS",
    category: "Tax Debt Relief",
    readTime: "8 min read",
    quickAnswer: {
      answer:
        "Per IRS policy statement IRM 1.2.1.5.18, the IRS generally requires the most recent six years of returns to be filed to consider you 'in compliance.' Filing earlier-year returns is not always required and can actually reopen closed periods. The right strategy depends on whether the IRS has filed Substitutes for Return (SFRs) on your behalf.",
      bullets: [
        "File the most recent six years first.",
        "If the IRS has filed an SFR, you can override it with a real return that includes your deductions.",
        "First-Time Penalty Abatement can wipe out failure-to-file penalties for one year of compliance.",
      ],
    },
    body: `If you've fallen years behind on filing, you are not as alone — or as doomed — as you think. The IRS deals with millions of unfiled returns. The system is built to bring people back into compliance, and there's a defined process for doing it correctly.

## How far back do you really need to file?

Per [IRM 1.2.1.5.18](https://www.irs.gov/irm/part1/irm_01-002-001) (IRS Policy Statement 5-133), the IRS generally requires the **most recent six years** of returns to be filed for you to be considered "in compliance." Filing earlier-year returns is **not always required** — and in some cases it can actually reopen periods the IRS has already closed.

| Years to file | Almost always | Sometimes | Almost never |
| --- | --- | --- | --- |
| Most recent 6 years | ✓ | | |
| Years with SFR already filed | | ✓ (to reduce assessed tax) | |
| Years > 6 with no IRS activity | | | ✓ |

## Substitute for Return (SFR): why the IRS-prepared return is your enemy

If you didn't file, the IRS may have filed a **Substitute for Return** on your behalf under [IRC § 6020(b)](https://www.law.cornell.edu/uscode/text/26/6020). SFRs are intentionally punitive — they include:

- **Single** filing status (even if you're married).
- **Standard deduction only** — no itemized deductions, no business expenses.
- **No dependents.**
- **No cost basis** on stock sales — the entire proceeds are taxed as gain.

The result: an artificially inflated balance, often two to ten times what you'd actually owe with a properly prepared return.

You can **override an SFR** by filing a real return for that year. The new return triggers an audit reconsideration, and the proper deductions and credits are applied.

## The right filing order

1. **Pull every wage & income transcript** for the years in question.
2. **Reconstruct deductions and expenses** — bank statements, mileage logs, 1099s sent and received.
3. **File the most recent six years** in order, oldest first.
4. **Override any SFR assessments** with properly prepared returns.
5. **Set up a resolution** for any resulting balance — installment agreement, CNC, or OIC.

## First-Time Penalty Abatement is on the table

Under the [First-Time Abatement (FTA) program](https://www.irs.gov/businesses/small-businesses-self-employed/first-time-penalty-abatement), the IRS will waive failure-to-file and failure-to-pay penalties for **one tax year** if you've been compliant for the three prior years and you're current on filing and payment obligations.

Critically: FTA can apply to the **first year** in your sequence of late returns. If you owe $40,000 in penalties across six years, FTA on the earliest year typically wipes out the largest chunk.

## Reasonable Cause abatement for everything else

Beyond FTA, [Reasonable Cause](https://www.irs.gov/businesses/small-businesses-self-employed/penalty-relief-due-to-reasonable-cause) can erase penalties when there's a documented reason — serious illness, death in the family, natural disaster, reliance on a tax professional, military service. A narrative letter with supporting documentation is required.

## Common mistakes that make it worse

- **Filing years > 6 unnecessarily** — opens periods the IRS has closed and can create new balances.
- **Filing without pulling transcripts** — guarantees mismatches that trigger CP2000s.
- **Filing in random order** — confuses the IRS account and delays processing.
- **Paying nothing while filing** — adds failure-to-pay penalties on top of failure-to-file.
- **Filing without a resolution plan** — large balances trigger immediate enforcement once assessed.

## What to do this week

If you haven't filed in years, call **(214) 646-3802**. Step one is pulling transcripts; step two is mapping the right six-year window; step three is building a resolution for the resulting balance — usually all within the same engagement.`,
    sources: [
      {
        label: "IRM 1.2.1.5.18 — Policy Statement 5-133 (Delinquent Returns)",
        url: "https://www.irs.gov/irm/part1/irm_01-002-001",
        publisher: "Internal Revenue Manual",
      },
      {
        label: "First-Time Penalty Abatement",
        url: "https://www.irs.gov/businesses/small-businesses-self-employed/first-time-penalty-abatement",
        publisher: "IRS",
      },
      {
        label: "Penalty Relief Due to Reasonable Cause",
        url: "https://www.irs.gov/businesses/small-businesses-self-employed/penalty-relief-due-to-reasonable-cause",
        publisher: "IRS",
      },
      {
        label: "IRC § 6020 — Returns prepared for or executed by Secretary",
        url: "https://www.law.cornell.edu/uscode/text/26/6020",
        publisher: "Cornell Legal Information Institute",
      },
    ],
    relatedSlugs: [
      "how-an-offer-in-compromise-actually-works",
      "owe-the-irs-more-than-10000",
      "irs-statute-of-limitations-csed-explained",
    ],
    meta: {
      title: "Unfiled Tax Returns: The Survival Guide",
      description:
        "The IRS requires only six years of back returns. Here's the right order, how to override SFRs, and how to wipe out penalties with First-Time Abatement.",
    },
  },
];

export const blogPosts: BlogPost[] = [...authoredPosts, ...importedBlogPosts].sort(
  (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
);

export function getBlogPost(slug: string): BlogPost | undefined {
  return blogPosts.find((p) => p.slug === slug);
}

export function getRelatedPosts(slug: string, max = 3): BlogPost[] {
  const post = getBlogPost(slug);
  if (!post) return [];
  const explicit = (post.relatedSlugs ?? [])
    .map((s) => blogPosts.find((p) => p.slug === s))
    .filter((p): p is BlogPost => !!p)
    .slice(0, max);
  if (explicit.length >= max) return explicit;
  // Fill with same-category posts, then any other posts.
  const seen = new Set([slug, ...explicit.map((p) => p.slug)]);
  const sameCat = blogPosts.filter((p) => !seen.has(p.slug) && p.category === post.category);
  const others = blogPosts.filter((p) => !seen.has(p.slug) && p.category !== post.category);
  return [...explicit, ...sameCat, ...others].slice(0, max);
}
